import React, { useState, useRef } from 'react';
import { connect } from 'react-redux';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';

import * as invoiceAdditionActions from '../../../redux/invoiceAdditions/actions';
import { validator, formatter } from '../../../utils';
import alertAndConfirmation from '../../../utils/AlertAndConfirmation';

import Spin from '../../../components/loading';

function AddInvoiceAddition(props) {
  const { error, loading, message, failed } = props;

  const [isActive, setIsActive] = useState(false);
  const [amount, setAmount] = useState('');
  const [title, setTitle] = useState('');
  const [isPercent, setIsPercent] = useState(false);
  const [showOnFactor, setShowOnFactor] = useState(true);
  const [amountInputInvalidMessage, setAmountInputInvalidMessage] = useState('');
  const [titleInputInvalidMessage, setTitleInputInvalidMessage] = useState('');

  const amountRef = useRef();
  const amountHelperTextRef = useRef();
  const amountLabelRef = useRef();

  const save = e => {
    e.preventDefault();

    if (!validateForm()) return;

    const data = {
      title,
      value: amount,
      isActive,
      isPercent,
      showOnFactor
    };

    props.actions.add(data).then(() => {
      alertAndConfirmation({
        type: 'success',
        title: locales('titles.insertedSuccessfully'),
        text: ''
      });

      props.close(true);
    });
  };

  const close = e => {
    e.preventDefault();

    props.close();
  };

  const onChangeAmount = e => {
    const val = e.target.value;

    if (!val || validator.isNumberMoreThanZero(val)) {
      setAmount(val);
      setAmountInputInvalidMessage();
    }
  };

  const onChangeTitle = e => {
    const val = event.target.value;

    if (!val || val.length <= 50) {
      setTitle(val);
      setTitleInputInvalidMessage();
    }
  };

  const onChangeIsPercent = e => {
    if (e.target.checked) {
      amountRef.current.placeholder = locales('messages.sample.amount.percent');
      amountLabelRef.current.innerText = locales('fields.percent');
      amountHelperTextRef.current.innerText = locales('messages.input.amount.as.percent');
    } else {
      amountRef.current.placeholder = locales('messages.sample.amount');
      amountLabelRef.current.innerText = locales('fields.amount');
      amountHelperTextRef.current.innerText = locales('messages.input.amount.as.toman');
    }

    setIsPercent(e.target.checked);
  };

  const validateForm = () => {
    const standardTitle = formatter.toStandard(title);
    const standardAmount = formatter.toStandard(amount);

    let isTitleFieldValid = false;
    let isAmountFieldValid = false;

    if (!standardTitle) {
      isTitleFieldValid = false;
      setTitleInputInvalidMessage(
        locales('messages.fieldValueIsRequired', {
          fieldName: locales('tables.title')
        })
      );
    } else {
      isTitleFieldValid = true;
      setTitleInputInvalidMessage('');
    }

    if (!standardAmount) {
      isAmountFieldValid = false;
      setAmountInputInvalidMessage(
        locales('messages.fieldValueIsRequired', {
          fieldName: isPercent ? locales('fields.percent') : locales('fields.amount')
        })
      );
    } else {
      if (isPercent && standardAmount > 100) {
        isAmountFieldValid = false;
        setAmountInputInvalidMessage(locales('messages.error.input.valid.percent'));
      } else {
        isAmountFieldValid = true;
        setAmountInputInvalidMessage('');
      }
    }

    return isTitleFieldValid && isAmountFieldValid;
  };

  return (
    <Modal size="xl" isOpen={props.isOpen}>
      <ModalHeader>
        <h4>{locales('titles.newInvoiceAdditions')}</h4>
        <span
          title={locales('labels.close.modal')}
          className="modal-close"
          onClick={close}
          disabled={loading}
        >
          close
        </span>
      </ModalHeader>
      <ModalBody>
        <Spin spinning={loading}>
          {error && <div className="alert alert-danger">{locales('messages.server.error')}</div>}

          {failed && <div className="alert alert-danger">{message}</div>}

          <form className="form-horizontal">
            <div className="form-row">
              <div className="form-group d-flex col required">
                <label htmlFor="title" className="rsee-form-label">
                  {locales('tables.title')}
                </label>
                <div className="full-width">
                  <input
                    name="title"
                    type="text"
                    className={`form-control ${!!titleInputInvalidMessage ? 'is-invalid' : ''}`}
                    placeholder={locales('messages.sample.invoice.addition.title')}
                    onChange={onChangeTitle}
                    value={title}
                    autoComplete="off"
                  />
                  <div className="invalid-feedback">{titleInputInvalidMessage}</div>
                </div>
              </div>
              <div className="form-group d-flex col required">
                <label htmlFor="amount" className="rsee-form-label" ref={amountLabelRef}>
                  {locales('fields.amount')}
                </label>
                <div className="full-width">
                  <input
                    name="amount"
                    type="text"
                    className={`form-control ${!!amountInputInvalidMessage ? 'is-invalid' : ''}`}
                    placeholder={locales('messages.sample.amount')}
                    onChange={onChangeAmount}
                    value={amount}
                    autoComplete="off"
                    ref={amountRef}
                  />
                  <small className="form-text text-muted" ref={amountHelperTextRef}>
                    {locales('messages.input.amount.as.toman')}
                  </small>
                  <div className="invalid-feedback">{amountInputInvalidMessage}</div>
                </div>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group form-check-inline">
                <label className="form-check-label rsee-form-check-label-in-modal-lg">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    onChange={e => setIsActive(e.target.checked)}
                    checked={isActive}
                  />
                  {locales('tables.activated')}
                </label>
              </div>
              <div className="form-group form-check-inline">
                <label className="form-check-label rsee-form-check-label-in-modal-lg">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    onChange={e => setShowOnFactor(e.target.checked)}
                    checked={showOnFactor}
                    disabled
                  />
                  {locales('labels.show.on.customer.invoice')}
                </label>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group form-check-inline">
                <label className="form-check-label rsee-form-check-label-in-modal-lg">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    onChange={onChangeIsPercent}
                    checked={isPercent}
                  />
                  {locales('tables.percent')}
                </label>
              </div>
            </div>
          </form>
        </Spin>
      </ModalBody>
      <ModalFooter>
        <button className="btn btn-success" onClick={save} disabled={loading}>
          <i className="fa fa-save"></i>
          {locales('labels.save')}
        </button>

        <button className="btn btn-light mx-1" onClick={close} disabled={loading}>
          {locales('labels.close.modal')}
        </button>
      </ModalFooter>
    </Modal>
  );
}

const mapStateToProps = state => {
  return {
    loading: state.invoiceAdditionsReducer.createInvoiceAdditionLoading,
    failed: state.invoiceAdditionsReducer.createInvoiceAdditionFailed,
    error: state.invoiceAdditionsReducer.createInvoiceAdditionError,
    message: state.invoiceAdditionsReducer.createInvoiceAdditionMessage
  };
};

const mapDispatchToProps = dispatch => {
  return {
    actions: {
      add: data => dispatch(invoiceAdditionActions.add(data))
    }
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(AddInvoiceAddition);
